import { useRef } from "react";
import {
  motion,
  useScroll,
  useVelocity,
  useSpring,
  useTransform,
  useMotionValue,
  useAnimationFrame,
  useReducedMotion,
  wrap,
} from "framer-motion";

const ITEMS = ["Smart Savings", "Better Benefits", "Hidden Opportunities", "A Healthier Bottom Line"];

const STYLES = [
  "text-navy",
  "text-transparent [-webkit-text-stroke:2px_#234D85]",
  "text-teal-dark",
  "text-transparent [-webkit-text-stroke:2px_#15B8A6]",
];

function Diamond() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" className="shrink-0" aria-hidden="true">
      <path d="M12 2 L17 12 L12 22 L7 12 Z" fill="#15B8A6" />
    </svg>
  );
}

function Sequence() {
  return (
    <>
      {ITEMS.map((item, i) => (
        <span key={i} className="flex items-center shrink-0">
          <span
            className={`px-5 md:px-9 font-display font-extrabold tracking-tightest whitespace-nowrap text-5xl md:text-7xl lg:text-8xl leading-none ${STYLES[i]}`}
          >
            {item}
          </span>
          <Diamond />
        </span>
      ))}
    </>
  );
}

export default function Marquee() {
  const reduced = useReducedMotion();
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 });
  const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 4], { clamp: false });
  const x = useTransform(baseX, (v) => `${wrap(-25, 0, v)}%`);
  const direction = useRef(1);

  useAnimationFrame((t, delta) => {
    if (reduced) return;
    let moveBy = direction.current * 1.6 * (delta / 1000);
    const vf = velocityFactor.get();
    if (vf < 0) direction.current = -1;
    else if (vf > 0) direction.current = 1;
    moveBy += direction.current * moveBy * Math.abs(vf);
    baseX.set(baseX.get() + moveBy);
  });

  if (reduced) {
    return (
      <section className="relative bg-offwhite border-y border-line py-10 overflow-hidden" data-testid="brand-type-band">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap gap-x-8 gap-y-3 justify-center">
          {ITEMS.map((item, i) => (
            <span key={item} className={`font-display font-extrabold tracking-tight text-3xl md:text-5xl ${STYLES[i]}`}>
              {item}
            </span>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-offwhite border-y border-line py-8 md:py-12 overflow-hidden" data-testid="brand-type-band">
      <motion.div className="flex w-max items-center" style={{ x }}>
        <Sequence />
        <Sequence />
        <Sequence />
        <Sequence />
      </motion.div>
      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-offwhite to-transparent" aria-hidden="true" />
      <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-offwhite to-transparent" aria-hidden="true" />
    </section>
  );
}
